import { type User as KnockUser } from "@knocklabs/node";
import { type Company, type CompensationReviewCampaign, type Prisma } from "@prisma/client";
import { absoluteRoute } from "~/lib/url";
import { formatExternalEmployeeName } from "~/services/external-employee";
import { formatUserFullName } from "~/services/user/utils";

export const selectUserForFormat = {
  id: true,
  email: true,
  profilePictureUrl: true,
  firstName: true,
  lastName: true,
  locale: true,
};

export const formatKnockUser = (user: Prisma.UserGetPayload<{ select: typeof selectUserForFormat }>): KnockUser => {
  return {
    id: user.id.toString(),
    name: formatUserFullName(user),
    email: user.email,
    avatar: user.profilePictureUrl,
    locale: user.locale.toLowerCase(),
  };
};

export const formatKnockTenant = (company: Pick<Company, "id" | "name">) => {
  return {
    id: company.id.toString(),
    name: company.name,
  };
};

export const formatCompensationReviewCampaign = (campaign: Pick<CompensationReviewCampaign, "id" | "name">) => {
  return {
    id: campaign.id.toString(),
    name: campaign.name,
    url: absoluteRoute({
      pathname: "/compensation-review/campaigns/[campaignId]",
      query: { campaignId: campaign.id.toString() },
    }),
  };
};

export const selectCompensationReviewEmployeeForFormat = {
  id: true,
  externalEmployee: {
    select: {
      employeeNumber: true,
      externalId: true,
      firstName: true,
      lastName: true,
    },
  },
} satisfies Prisma.CompensationReviewEmployeeSelect;

export const formatCompensationReviewEmployee = (
  employee: Prisma.CompensationReviewEmployeeGetPayload<{
    select: typeof selectCompensationReviewEmployeeForFormat;
  }>
) => {
  return {
    id: employee.id.toString(),
    name: formatExternalEmployeeName(employee.externalEmployee),
  };
};

export const selectCompensationReviewReviewerForFormat = {
  id: true,
  externalEmployee: {
    select: {
      employeeNumber: true,
      externalId: true,
      firstName: true,
      lastName: true,
    },
  },
} satisfies Prisma.CompensationReviewReviewerSelect;

export const formatCompensationReviewReviewer = (
  reviewer: Prisma.CompensationReviewReviewerGetPayload<{
    select: typeof selectCompensationReviewReviewerForFormat;
  }>
) => {
  return {
    id: reviewer.id.toString(),
    name: formatExternalEmployeeName(reviewer.externalEmployee),
  };
};
